import { useEffect, useState, useCallback, useRef } from "react";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import { chunkedUpload } from "../lib/chunkedUpload";

export function useBankTransactions() {
  const [driveStatus, setDriveStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);       // 0-100 for current file upload
  const [phase, setPhase] = useState("");            // "upload" | "parse" | ""
  const [queue, setQueue] = useState(null);          // { index, total, name }
  const [statements, setStatements] = useState([]);
  const [txns, setTxns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [banks, setBanks] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tamperNotice, setTamperNotice] = useState(null);
  const [openTally, setOpenTally] = useState(null);
  const [expenseForm, setExpenseForm] = useState(null); // { txnId, category, reason, lr_number }
  const [lrs, setLrs] = useState([]);
  const [people, setPeople] = useState([]);
  const [bankPrompts, setBankPrompts] = useState([]); // statements uploaded with an undetected bank
  const [editStmt, setEditStmt] = useState(null);      // statement being edited via the pencil
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showMerge, setShowMerge] = useState(false);
  const [forceImport, setForceImport] = useState(false);
  const [accountBalances, setAccountBalances] = useState([]);
  const [monthlySpend, setMonthlySpend] = useState({ series: [], categories: [] });

  const [fBank, setFBank] = useState("");
  const [fAccount, setFAccount] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fType, setFType] = useState("");
  const [fStart, setFStart] = useState("");
  const [fEnd, setFEnd] = useState("");
  const [fMin, setFMin] = useState("");
  const [fMax, setFMax] = useState("");
  const [fQuery, setFQuery] = useState("");
  const abortRef = useRef(null);

  useEffect(() => {
    api.get("/drive/status").then((r) => setDriveStatus(r.data)).catch(() => setDriveStatus({ connected: false }));
    api.get("/lrs").then((r) => setLrs(r.data || [])).catch(() => {});
    api.get("/bank/personal-people").then((r) => setPeople(r.data.people || [])).catch(() => {});
  }, []);

  const loadStatements = useCallback(() => {
    api.get("/bank/statements").then((r) => setStatements(r.data)).catch(() => {});
    api.get("/bank-accounts").then((r) => setBankAccounts(r.data || [])).catch(() => {});
    api.get("/bank/account-balances").then((r) => setAccountBalances(r.data.accounts || [])).catch(() => {});
  }, []);

  const loadMonthlySpend = useCallback(() => {
    const params = fAccount ? { account: fAccount } : {};
    api.get("/bank/monthly-spend", { params })
      .then((r) => setMonthlySpend(r.data || { series: [], categories: [] }))
      .catch(() => {});
  }, [fAccount]);

  const loadTxns = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    const params = {};
    if (fAccount) params.account = fAccount;
    else if (fBank) params.bank = fBank;
    if (fStatus) params.status = fStatus;
    if (fType) params.type = fType;
    if (fStart) params.start = fStart;
    if (fEnd) params.end = fEnd;
    if (fMin !== "") params.min_amount = fMin;
    if (fMax !== "") params.max_amount = fMax;
    if (fQuery.trim()) params.q = fQuery.trim();
    api.get("/bank/transactions", { params, signal: controller.signal })
      .then((r) => {
        setTxns(r.data.transactions); setSummary(r.data.summary);
        setBanks(r.data.banks); setAccounts(r.data.accounts || []); setLoading(false);
      })
      .catch((e) => {
        if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return; // superseded by a newer request
        toast.error("Failed to load transactions");
        setLoading(false);
      });
  }, [fBank, fAccount, fStatus, fType, fStart, fEnd, fMin, fMax, fQuery]);

  useEffect(() => { loadStatements(); }, [loadStatements]);
  useEffect(() => { loadTxns(); }, [loadTxns]);
  useEffect(() => { loadMonthlySpend(); }, [loadMonthlySpend]);

  async function onUpload(e) {
    // Snapshot the FileList to a real array BEFORE clearing the input value,
    // otherwise the live FileList reference becomes empty and the upload no-ops.
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    await uploadStatements(files);
  }

  // Backward-compatible single-file entry point.
  async function uploadStatement(file) {
    await uploadStatements(file ? [file] : []);
  }

  // Announce a finished (background-parsed) statement.
  function announceDone(s, name) {
    const inserted = s.txn_count || 0;
    const skipped = s.skipped_count || 0;
    if (inserted === 0 && skipped > 0) {
      toast.info(`${name}: already imported — ${skipped} duplicate row(s) skipped`);
    } else {
      const bank = s.bank_name || name;
      toast.success(`${bank}: ${inserted} new transaction(s)` + (skipped ? `, ${skipped} duplicate(s) skipped` : ""));
    }
    if (s.auto_merged && s.account_label) {
      toast.info(`Merged into existing account ${s.account_label}`);
    }
    if ((s.tamper_issues || []).length > 0) setTamperNotice(s);
    if (s.needs_bank_info) setBankPrompts((prev) => (prev.some((p) => p.id === s.id) ? prev : [...prev, s]));
  }

  // Poll uploaded statements until each finishes parsing in the background.
  async function pollProcessing(pending) {
    const remaining = new Map(pending.map((p) => [p.id, p.name]));
    const deadline = Date.now() + 8 * 60 * 1000; // give big PDFs plenty of room
    while (remaining.size && Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 2500));
      for (const [id, name] of Array.from(remaining.entries())) {
        try {
          const { data: s } = await api.get(`/bank/statements/${id}`);
          if (s.status === "done") { remaining.delete(id); announceDone(s, name); }
          else if (s.status === "failed") { remaining.delete(id); toast.error(`${name}: ${s.error || "Could not parse statement"}`); }
        } catch { /* transient; keep polling */ }
      }
      loadStatements(); // reflect processing → done live in the list
    }
    for (const [, name] of remaining.entries()) {
      toast.warning(`${name}: still processing — it'll appear once parsing finishes`);
    }
  }

  // Import one or more statements sequentially, with live progress. Parsing then
  // happens in the background so the upload request never hits the 120s proxy timeout.
  async function uploadStatements(fileList) {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setUploading(true); setTamperNotice(null);
    const pending = [];   // { id, name } statements now parsing in the background
    let failed = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setQueue({ index: i + 1, total: files.length, name: file.name });
      setPhase("upload"); setProgress(0);
      try {
        const data = await chunkedUpload(file, { kind: "bank", force: forceImport }, (p) => {
          setProgress(p);
          if (p >= 100) setPhase("parse");
        });
        setPhase("parse"); setProgress(100);
        if (data.statement?.id) pending.push({ id: data.statement.id, name: file.name });
      } catch (err) {
        failed++;
        const msg = err?.response?.data?.detail || "Upload failed";
        toast.error(files.length > 1 ? `${file.name}: ${msg}` : msg);
      }
    }

    if (pending.length) {
      toast.info(pending.length > 1
        ? `${pending.length} statement(s) uploaded — parsing in the background…`
        : `"${pending[0].name}" uploaded — parsing in the background…`);
      loadStatements();               // show the "Processing…" rows immediately
      await pollProcessing(pending);  // then flip to results as each finishes
    } else if (failed && files.length > 1) {
      toast.warning(`${failed} of ${files.length} upload(s) failed`);
    }

    setUploading(false); setPhase(""); setProgress(0); setQueue(null);
    loadStatements(); loadTxns(); loadMonthlySpend();
  }

  async function flagStatement(stmt, flagged, reason = "") {
    try {
      await api.put(`/bank/statements/${stmt.id}/flag`, { flagged, reason });
      toast.success(flagged ? "Marked as parsed incorrectly" : "Parse flag cleared");
      loadStatements();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not update"); }
  }

  async function reparseStatement(stmt) {
    try {
      await api.post(`/bank/statements/${stmt.id}/reparse`);
      toast.info(`Re-parsing "${stmt.filename || stmt.account_label}" in the background…`);
      loadStatements();
      await pollProcessing([{ id: stmt.id, name: stmt.filename || stmt.account_label || "Statement" }]);
      loadStatements(); loadTxns(); loadMonthlySpend();
    } catch (err) { toast.error(err?.response?.data?.detail || "Re-parse failed"); }
  }

  async function setStatementBank(stmt, { bank_name, account_number, nickname }) {
    try {
      await api.put(`/bank/statements/${stmt.id}/bank`, {
        bank_name: (bank_name || "").trim(),
        account_number: (account_number || "").trim(),
        nickname: (nickname || "").trim(),
      });
      toast.success(`Saved · ${(nickname || bank_name || "").trim()}`);
      setBankPrompts((prev) => prev.filter((s) => s.id !== stmt.id));
      setEditStmt(null);
      loadStatements(); loadTxns();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not update bank"); }
  }

  function resetFilters() {
    setFBank(""); setFAccount(""); setFStatus(""); setFType("");
    setFStart(""); setFEnd(""); setFMin(""); setFMax(""); setFQuery("");
  }

  async function mergeAccounts(sourceId, targetId) {
    try {
      const r = await api.post(`/bank/accounts/merge`, { source_id: sourceId, target_id: targetId });
      const { moved_txns, removed_duplicates, target_label } = r.data;
      toast.success(`Merged into ${target_label} · ${moved_txns} moved` + (removed_duplicates ? `, ${removed_duplicates} duplicate(s) removed` : ""));
      setShowMerge(false);
      resetFilters();
      loadStatements(); loadTxns();
    } catch (err) { toast.error(err?.response?.data?.detail || "Merge failed"); }
  }

  async function tally(txn, matched) {
    try {
      await api.post(`/bank/transactions/${txn.id}/tally`, matched || { matched_type: "manual" });
      setOpenTally(null);
      loadTxns();
      toast.success("Tallied");
    } catch { toast.error("Failed to tally"); }
  }

  async function tallyPersonal(txn, payload) {
    try {
      await api.post(`/bank/transactions/${txn.id}/tally-personal`, payload);
      setOpenTally(null);
      loadTxns();
      toast.success("Tallied to personal account");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to tally"); }
  }

  const [autoTallyingTransfers, setAutoTallyingTransfers] = useState(false);
  const [transferReview, setTransferReview] = useState(null); // { pairs } | null
  async function openTransferReview() {
    setAutoTallyingTransfers(true);
    try {
      const { data } = await api.get("/bank/transfer-suggestions");
      if (!data.pairs.length) { toast.info("No matching internal transfers found."); return; }
      setTransferReview({ pairs: data.pairs });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load transfer suggestions");
    } finally { setAutoTallyingTransfers(false); }
  }
  async function confirmTransfers(selectedPairs) {
    try {
      const payload = { pairs: selectedPairs.map((p) => ({ debit_id: p.debit.id, credit_id: p.credit.id })) };
      const { data } = await api.post("/bank/auto-tally-transfers", payload);
      toast.success(`Tallied ${data.pairs} internal transfer(s) — ${data.rows} rows`);
      setTransferReview(null);
      loadTxns();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to tally transfers");
    }
  }

  async function tallyRef(txn, refType, refNumber) {
    const number = (refNumber || "").trim();
    if (!number) { toast.error("Enter an LR / invoice number"); return; }
    try {
      const r = await api.post(`/bank/transactions/${txn.id}/tally-ref`, {
        ref_type: refType, ref_number: number,
      });
      setOpenTally(null);
      loadTxns();
      if (r.data.pending) {
        toast.success(`Tallied to ${r.data.label} — it'll auto-link once that ${refType === "lr" ? "LR" : "invoice"} is created (added a board reminder).`);
      } else {
        toast.success(`Tallied to ${r.data.label}`);
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to tally"); }
  }

  async function untally(txn) {
    try {
      await api.post(`/bank/transactions/${txn.id}/untally`);
      loadTxns();
    } catch { toast.error("Failed to untally"); }
  }

  async function createExpense(txn) {
    try {
      const allocations = (expenseForm.allocations || [])
        .filter((a) => (a.lr_number || "").trim())
        .map((a) => ({ lr_number: a.lr_number.trim(), amount: Number(a.amount) || 0 }));
      await api.post(`/bank/transactions/${txn.id}/create-expense`, {
        category: expenseForm.category, reason: expenseForm.reason,
        lr_number: expenseForm.lr_number || "", lr_allocations: allocations,
      });
      setExpenseForm(null); setOpenTally(null); loadTxns();
      toast.success("Expense created & row tallied");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to create expense"); }
  }

  async function removeStatement(s) {
    if (!window.confirm(`Delete "${s.filename}" (${s.bank_name}) and its ${s.txn_count} transaction(s)?`)) return;
    try {
      await api.delete(`/bank/statements/${s.id}`);
      toast.success("Statement deleted");
      loadStatements(); loadTxns();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  }

  function exportXlsx() {
    const params = new URLSearchParams();
    if (fBank) params.set("bank", fBank);
    if (fStatus) params.set("status", fStatus);
    window.open(`${API}/export/bank-transactions.xlsx?${params.toString()}`, "_blank");
  }

  const [autoTallying, setAutoTallying] = useState(false);
  async function autoTally() {
    const scope = fAccount ? ` in ${fAccount}` : "";
    if (!window.confirm(`Auto-create expenses for all untallied Fuel & Toll debits${scope}? You can edit or delete them later.`)) return;
    setAutoTallying(true);
    try {
      const { data } = await api.post("/bank/auto-tally", { categories: ["Fuel", "Toll"], account: fAccount || null });
      if (data.created > 0) {
        const parts = Object.entries(data.by_category || {}).map(([k, v]) => `${v} ${k.toLowerCase()}`).join(", ");
        toast.success(`Auto-tallied ${data.created} row(s)${parts ? ` — ${parts}` : ""}`);
      } else {
        toast.info("No untallied fuel or toll debits found to auto-tally.");
      }
      loadTxns();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auto-tally failed");
    } finally { setAutoTallying(false); }
  }

  return {
    driveStatus, uploading, progress, phase, queue, statements, txns, summary, banks, accounts, loading,
    tamperNotice, setTamperNotice, openTally, setOpenTally, expenseForm, setExpenseForm,
    fBank, setFBank, fAccount, setFAccount, fStatus, setFStatus, fType, setFType,
    fStart, setFStart, fEnd, setFEnd, fMin, setFMin, fMax, setFMax, fQuery, setFQuery, resetFilters,
    bankPrompts, setBankPrompts, editStmt, setEditStmt, setStatementBank,
    bankAccounts, showMerge, setShowMerge, mergeAccounts,
    forceImport, setForceImport, flagStatement, reparseStatement,
    accountBalances, monthlySpend,
    onUpload, uploadStatement, uploadStatements, tally, tallyRef, untally, createExpense, removeStatement, exportXlsx,
    autoTally, autoTallying,
    autoTallyTransfers: openTransferReview, autoTallyingTransfers, tallyPersonal, people,
    transferReview, setTransferReview, confirmTransfers,
    lrs,
  };
}
